import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Megaphone, Plus, Square, Clock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/formatters';
import { 
  createAnnouncement, 
  getOrganizationAnnouncements, 
  stopAnnouncement,
  type Announcement 
} from '@/services/announcementService';

export const AnnouncementManagement = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comments, setComments] = useState('');
  const [showForm, setShowForm] = useState(false);

  const fetchAnnouncements = async () => {
    if (!profile?.organization_id) return;
    
    try {
      const data = await getOrganizationAnnouncements(profile.organization_id);
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast({
        title: "Error",
        description: "Failed to load announcements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [profile?.organization_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profile?.organization_id || !profile.id || !comments.trim()) {
      return;
    }

    setSubmitting(true);
    
    try {
      await createAnnouncement({
        organisation_id: profile.organization_id,
        comments: comments.trim(),
        created_by: profile.id,
      });
      
      toast({
        title: "Success",
        description: "Announcement created successfully",
      });
      
      setComments('');
      setShowForm(false);
      fetchAnnouncements();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async (announcementId: string) => {
    try {
      await stopAnnouncement(announcementId);
      
      toast({
        title: "Success",
        description: "Announcement stopped successfully",
      });
      
      fetchAnnouncements();
    } catch (error: any) {
      toast({
        title: "Error", 
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const activeAnnouncements = announcements.filter(a => 
    a.is_active && new Date(a.end_time) > new Date()
  );

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Announcement Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">Loading announcements...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          Announcement Management
        </CardTitle>
        <CardDescription>
          Create and manage announcements for your organization
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active Count Alert */}
        {activeAnnouncements.length > 0 && (
          <Alert>
            <Megaphone className="h-4 w-4" />
            <AlertDescription>
              You have {activeAnnouncements.length}/5 active announcements. 
              Each announcement automatically expires after 10 hours.
            </AlertDescription>
          </Alert>
        )}

        {/* Create Announcement Form */}
        {!showForm ? (
          <Button 
            onClick={() => setShowForm(true)} 
            className="w-full"
            disabled={activeAnnouncements.length >= 5}
          >
            <Plus className="h-4 w-4 mr-2" />
            {activeAnnouncements.length >= 5 ? 'Maximum 5 announcements reached' : 'Create New Announcement'}
          </Button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="comments">Announcement Text</Label>
              <Textarea
                id="comments"
                placeholder="Enter your announcement message..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                required
                rows={4}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                type="submit" 
                disabled={submitting || !comments.trim()}
                className="flex-1"
              >
                {submitting ? 'Creating...' : 'Create Announcement'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowForm(false);
                  setComments('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Announcements List */}
        <div className="space-y-3">
          <h3 className="font-medium">Your Announcements</h3>
          
          {announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No announcements yet. Create your first announcement above.
            </div>
          ) : (
            <div className="space-y-3">
              {announcements.map((announcement) => {
                const isActive = announcement.is_active && new Date(announcement.end_time) > new Date();
                const isExpired = new Date(announcement.end_time) <= new Date();
                
                return (
                  <div key={announcement.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={isActive ? "default" : "secondary"}>
                            {isActive ? "Active" : isExpired ? "Expired" : "Stopped"}
                          </Badge>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Created: {formatDateTime(announcement.created_at)}
                          </div>
                        </div>
                        
                        <p className="text-sm mb-2">{announcement.comments}</p>
                        
                        <div className="text-xs text-muted-foreground">
                          Expires: {formatDateTime(announcement.end_time)}
                        </div>
                      </div>
                      
                      {isActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStop(announcement.id)}
                        >
                          <Square className="h-3 w-3 mr-1" />
                          Stop
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};